import React, { useCallback, useEffect, useRef, useState } from "react";
import MypageLayout from "./MypageLayout";
import { emitToast } from "./Toast";
import Markdown from "./Markdown";
import MonthPicker from "./MonthPicker";
import StatusBadge from "./StatusBadge";
import { WD, pad2, buildMonthCells } from "./dateUtil";
import ErrorBar from "./ErrorBar";
import { refreshBadgeCounts } from "./notifications";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import ConfirmDialog from "./ConfirmDialog";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// 経営者向け「勤怠一覧」（会議2026-07で確定）。
//  ・左：1行=1日付、列=日付→社員(各社員 出勤〜退勤)→日報(詳細ボタン)。勤務形態カラムは廃止。
//  ・右パネル（2モード）：
//     - 既定＝申請リスト（休み/時間訂正/残業の承認待ちをまとめて表示）
//     - 「詳細」ボタン → その日の日報（社員をラジオで切替）
//     - 社員の出勤〜終了セルを押す → 申請リストへ戻る
// 打刻は各社員が LINE(LIFF) で行い、ここは閲覧のみ。
const API_URL = process.env.REACT_APP_API_URL || "";
const LEAVE_TYPE = { paid: "有給", absence: "欠勤", special: "特別" };
const APP_KIND = { leave: "休み", correction: "時間訂正", overtime: "残業" };
// カレンダーの承認済み休みチップ（有給/特別＝ベージュ leave、欠勤＝赤 absence）。ty=種別文字、label=ツールチップ。
const LEAVE_CHIP = {
  paid: { cls: "leave", ty: "有", label: "有給" },
  special: { cls: "leave", ty: "特", label: "特別" },
  absence: { cls: "absence", ty: "欠", label: "欠勤" },
};
// 申請種類ごとの承認/却下エンドポイント（既存の承認画面と同じ decide API を使う）。
const DECIDE_PATH = {
  leave: "/api/employee/leave/decide",
  correction: "/api/employee/time-correction/decide",
  overtime: "/api/employee/overtime/decide",
};
// 申請リストのフィルタタブ（既定＝未承認）。
const APP_FILTERS = [
  { key: "pending", text: "未承認" },
  { key: "approved", text: "承認済" },
  { key: "rejected", text: "却下" },
  { key: "all", text: "すべて" },
];

function ymNow() { const d = new Date(); return `${d.getFullYear()}${pad2(d.getMonth() + 1)}`; }
// MonthPicker は "YYYY-MM"、本ビューの内部は "YYYYMM"。境界で相互変換する。
const ymDash = (s) => `${s.slice(0, 4)}-${s.slice(4, 6)}`;
const ymPlain = (s) => `${s.slice(0, 4)}${s.slice(5, 7)}`;
function dateShort(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? dateStr : `${d.getMonth() + 1}/${d.getDate()}`;
}
// 申請一覧の日付は年＋曜日を含めて表示する（例: 2026/5/2(金)）。
function dateFull(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? dateStr : `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}(${WD[d.getDay()]})`;
}
function weekdayJa(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? "" : WD[d.getDay()];
}
function timeOnly(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
// 社員セル：出勤〜退勤（両方）。勤務中は「〜勤務中」、休み・未出勤・未来はその表示。
function workText(c) {
  if (!c) return "-";
  if (c.status === "dayoff") return "休み";
  if (c.status === "none") return "-";
  if (c.status === "open") return `${timeOnly(c.clock_in)}〜勤務中`;
  return `${timeOnly(c.clock_in)}〜${timeOnly(c.clock_out)}`;
}
// 申請の表示用（種類ごとに日付・要約を整形）。
function appDate(a) {
  if (a.kind === "leave") return a.date_from === a.date_to ? dateFull(a.date_from) : `${dateFull(a.date_from)}〜${dateFull(a.date_to)}`;
  // 時間訂正の対象日は BE の target_date（対象日をJSTで解決済み）を使う。new_value は
  // UTCの日時文字列で、slice(0,10) するとオフセットが捨てられて 0〜9時(JST)の訂正が前日
  // 扱いになる。target_date は打刻無し日=申請日・打刻あり=出勤日を正しく指すので、
  // 深夜またぎの退勤訂正でも正しい勤務日になる（ApplyHistory/承認画面と同じフィールド）。
  if (a.kind === "correction") return dateFull(a.target_date || "");
  return dateFull(a.date);
}
function appSummary(a) {
  if (a.kind === "leave") return `${LEAVE_TYPE[a.type] || a.type}／${a.reason || "-"}`;
  if (a.kind === "correction") return `${a.field === "clock_in" ? "出勤" : "退勤"} ${timeOnly(a.old_value)}→${timeOnly(a.new_value)}／${a.reason || "-"}`;
  // 残業は開始/終了から残業時間を計算（新仕様＝手動申請。minutes は持たない）。
  const [sh, sm] = (a.start || "0:0").split(":").map(Number);
  const [eh, em] = (a.end || "0:0").split(":").map(Number);
  const mins = Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
  const h = Math.floor(mins / 60), m = mins % 60;
  const dur = `${h > 0 ? `${h}時間` : ""}${m > 0 ? `${m}分` : (h > 0 ? "" : "0分")}`;
  return `残業 ${a.start || "-"}〜${a.end || "-"}（${dur}）／${a.reason || "-"}`;
}
// 申請の対象日付範囲（休みは期間、時間訂正/残業は単日）。カレンダーのハイライト・月ジャンプに使う。
function appRange(a) {
  if (a.kind === "leave") return { from: a.date_from, to: a.date_to };
  // カレンダーのハイライト・月ジャンプも target_date（JST対象日）で揃える（appDate と同じ理由）。
  if (a.kind === "correction") { const d = a.target_date || ""; return { from: d, to: d }; }
  return { from: a.date, to: a.date };
}
function appYm(a) { const f = appRange(a).from || ""; return `${f.slice(0, 4)}${f.slice(5, 7)}`; }
function todayYmd() { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }

// 左カラム上部の月カレンダー（シフト×勤怠ロスター）。
//  ・シフト出勤の日は全アクティブ社員をドット＋フルネームで縦積み（過去/勤務中/予定は区別しない）。
//  ・承認済みの休みだけチップ化（有給/特別＝同色ベージュ、欠勤＝赤）。申請中は名前を変えない。
//  ・その日に未承認の申請があれば日付を黄丸に（クリックで右の申請一覧へスクロール）。
//  ・土日・祝日はシフト休として全員非表示（祝日は休日名のみ）。本日はセルの枠のみ（黒丸なし）。
// 名前/チップのクリック＝その社員のその日の日報へ、セル背景のクリック＝申請一覧へ（従来挙動）。
// ロスターカレンダーの氏名表示：PCは氏名フル、スマホ(≤860px)は姓のみ（列が狭く氏名が縦折り返す対策）。
// 出し分けは CSS の 860px ブレークで行う（両方描画し display で切替）。last_name はAPIが姓を別途返す。
function RcalName({ emp }) {
  return (
    <>
      <span className="mp-rcal-nm-full">{emp.name}</span>
      <span className="mp-rcal-nm-short">{emp.last_name || emp.name}</span>
    </>
  );
}

function RosterCalendar({ ym, employees, holidays, approvedByDay, workedByDay, holidayWork, appDays, range, onDayClick }) {
  const year = parseInt(ym.slice(0, 4), 10);
  const month = parseInt(ym.slice(4, 6), 10);
  const today = todayYmd();
  const cells = buildMonthCells(year, month);
  const ds = (d) => `${year}-${pad2(month)}-${pad2(d)}`;
  const inRange = (d) => range && range.from && ds(d) >= range.from && ds(d) <= range.to;

  return (
    <div className="mp-rcal">
      <div className="mp-rcal-grid mp-rcal-head">
        {WD.map((w, i) => (
          <div key={w} className={`mp-rcal-wd${i === 0 ? " mp-rcal-sun" : ""}${i === 6 ? " mp-rcal-sat" : ""}`}>{w}</div>
        ))}
      </div>
      <div className="mp-rcal-grid">
        {cells.map((d, i) => {
          if (d == null) return <div key={i} className="mp-rcal-cell mp-rcal-empty" />;
          const dstr = ds(d);
          const wd = new Date(`${dstr}T00:00:00`).getDay();
          const holName = holidays[dstr];
          const isHol = !!holName;
          // 会社休＝祝日＋週末のみ（誰か個人のシフトには依存しない。シフトは社員個人のもので、経営者にシフトは無い）。
          const isOff = isHol || wd === 0 || wd === 6;
          const isToday = dstr === today;
          const hasApp = appDays && appDays.has(dstr);
          const approved = approvedByDay[dstr] || {};
          const cls = [
            "mp-rcal-cell",
            isOff ? "mp-rcal-off" : "",
            isHol ? "mp-rcal-hol" : "",
            !isHol && wd === 0 ? "mp-rcal-sun" : "",
            !isHol && wd === 6 ? "mp-rcal-sat" : "",
            inRange(d) ? "mp-rcal-hl" : "",
            isToday ? "mp-rcal-today" : "",
          ].filter(Boolean).join(" ");
          return (
            <div
              key={i}
              className={cls}
              role="button"
              tabIndex={0}
              onClick={() => onDayClick(dstr)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onDayClick(dstr); } }}
            >
              <div className="mp-rcal-top">
                {isHol && <span className="mp-rcal-holname">{holName}</span>}
                <span className={`mp-rcal-day${hasApp ? " mp-rcal-app" : ""}`}>{d}</span>
              </div>
              <div className="mp-rcal-stack">
                {employees.map((e) => {
                  const lv = approved[e.id];
                  const didWork = workedByDay[dstr] && workedByDay[dstr].has(e.id);
                  // 祝日でも「祝日出勤予定(holidayWork)」がある社員は表示する（未来日=予定でも出す）。
                  const scheduledHol = holidayWork[dstr] && holidayWork[dstr].includes(e.id);
                  // オフ日（土日・祝日）は、出勤登録・祝日出勤予定・承認休みがある人だけ表示。
                  // シフト出勤日（!isOff）は全員表示（過去=実績/未来=予定を区別しない）。
                  if (!lv && !didWork && !scheduledHol && isOff) return null;
                  // 名前・チップは表示のみ（クリックで日報は開かない。日報は表の「詳細」から）。
                  if (lv) {
                    return (
                      <span key={e.id} className={`mp-rcal-chip mp-rcal-${lv.cls}`} title={`${e.name} ${lv.label}`}>
                        <span className="mp-rcal-dot" /><RcalName emp={e} /><span className="mp-rcal-ty">{lv.ty}</span>
                      </span>
                    );
                  }
                  return (
                    <span key={e.id} className="mp-rcal-nm" title={e.name}>
                      <span className="mp-rcal-dot" /><RcalName emp={e} />
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AttendanceMonthly() {
  const [ym, setYm] = useState(ymNow);
  const [data, setData] = useState({ employees: [], days: [] });
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 右パネル：apps=申請リスト（既定） / report=日報
  const [panelMode, setPanelMode] = useState("apps");
  const [reportDate, setReportDate] = useState(null);
  const [reportEmpId, setReportEmpId] = useState(null);
  // カレンダーのハイライト（選択中の申請の対象日）と選択中の申請キー。
  const [highlight, setHighlight] = useState(null);
  const [selectedAppKey, setSelectedAppKey] = useState(null);
  // 承認/却下：確認ダイアログ（{app, action}）、処理中の申請キー、成功メッセージ。
  const [confirm, setConfirm] = useState(null);
  const [decidingKey, setDecidingKey] = useState(null);
  // 申請リストのフィルタ（申請中／承認済み／却下／すべて）。
  const [appFilter, setAppFilter] = useState("pending");
  // スクロール対象の申請キー。フィルタ切替の再レンダー確定後にスクロールするためstate経由にする。
  const [scrollTarget, setScrollTarget] = useState(null);
  const detailRef = useRef(null);
  const applistRef = useRef(null);

  const fetchMonth = useCallback(async (m) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/employee/attendances/by-month/?ym=${m}`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (res.status === 403) { setError("経営者権限が必要です。"); return; }
      if (!res.ok) throw new Error("取得に失敗しました");
      const d = await res.json();
      setData({ employees: d.employees || [], days: d.days || [], holidays: d.holidays || {}, holidayWork: d.holidayWork || {} });
    } catch (e) {
      setError("勤怠の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchApps = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/employee/applications/`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setApps(d.results || []); }
    } catch (e) { /* 申請取得失敗は右パネルの空表示で吸収 */ }
  }, []);

  useEffect(() => { fetchMonth(ym); }, [ym, fetchMonth]);
  useEffect(() => { fetchApps(); }, [fetchApps]);

  const { employees, days } = data;
  const reportDay = days.find((d) => d.date === reportDate) || null;
  const reportCell = reportDay ? reportDay.cells.find((c) => c.employee_id === reportEmpId) : null;

  // 日報（詳細）を開く：その日付＋既定社員（日報がある社員を優先）。
  const openReport = (day) => {
    setPanelMode("report");
    setReportDate(day.date);
    setHighlight({ from: day.date, to: day.date });
    const withRep = day.cells.find((c) => c.daily_report) || day.cells[0];
    setReportEmpId(withRep ? withRep.employee_id : null);
    // モバイルは右パネルがリスト下に出るのでその位置へスクロール。
    window.setTimeout(() => {
      if (detailRef.current && window.innerWidth <= 860) detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };
  // グリッドのセルクリック：右パネルを申請リストに戻す。
  // 申請ありのセルは、その社員・その日の申請を選択＋ハイライトし、リストを該当申請までスクロールする
  //（カレンダーの日付クリックと同じ挙動。ただし社員も特定できるので該当社員の申請に絞る）。
  const onCellClick = (employeeId, dstr, hasApp) => {
    setPanelMode("apps");
    if (!hasApp) return;
    const inDay = (x) => { const r = appRange(x); return r.from && dstr >= r.from && dstr <= (r.to || r.from); };
    const forCell = apps.filter((x) => x.employee_id === employeeId && inDay(x));
    if (forCell.length === 0) return;
    // 未承認を優先（カレンダーと同様）。無ければ先頭の申請。
    const hit = forCell.find((x) => x.status === "pending") || forCell[0];
    // 選択した申請が確実にリストに出るタブへ切替（未承認は未承認タブ、それ以外はすべて）。
    setAppFilter(hit.status === "pending" ? "pending" : "all");
    const key = `${hit.kind}-${hit.id}`;
    setSelectedAppKey(key);
    setHighlight(appRange(hit));
    setScrollTarget({ key });
  };
  // 申請を選ぶ：カレンダーをその月へジャンプし、対象日をハイライト。
  const selectApp = (a) => {
    setPanelMode("apps");
    setHighlight(appRange(a));
    setSelectedAppKey(`${a.kind}-${a.id}`);
    setYm(appYm(a));
  };

  // 承認/却下を実行：decide API を叩き、成功したらそのカードの status を更新（楽観更新）。
  // 承認済み/却下は該当タブへ移動する（申請中タブからは外れる）。失敗時は再取得でロールバック。
  const doDecide = async () => {
    if (!confirm) return;
    const { app, action } = confirm;
    const key = `${app.kind}-${app.id}`;
    const nextStatus = action === "approve" ? "approved" : "rejected";
    setDecidingKey(key);
    setError("");
    try {
      const res = await fetch(`${API_URL}${DECIDE_PATH[app.kind]}/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ id: app.id, action }),
      });
      // BEの理由をそのまま出す（例:「申請中の申請のみ承認/却下できます」＝他の人が先に処理した）。
      if (!res.ok) { setError(await apiErrorMessage(res, "処理に失敗しました。")); return; }
      // 楽観更新：status を書き換える（除去せず承認済み/却下として残す）。
      setApps((prev) => prev.map((x) => (`${x.kind}-${x.id}` === key ? { ...x, status: nextStatus } : x)));
      // 承認待ちが1件減った＝サイドバーの勤怠履歴バッジも減らす（状態由来）。
      refreshBadgeCounts();
      emitToast(action === "approve" ? "承認しました。" : "却下しました。");
    } catch (e) {
      setError("処理に失敗しました。");
      fetchApps();
    } finally {
      setDecidingKey(null);
      setConfirm(null);
    }
  };

  // フィルタタブで絞り込み → 日付順に並べる（カレンダー↔リストの対応を分かりやすく）。
  const filteredApps = appFilter === "all" ? apps : apps.filter((a) => a.status === appFilter);
  const sortedApps = [...filteredApps].sort((a, b) => (appRange(a).from || "").localeCompare(appRange(b).from || ""));
  // 表示中の月で「未承認の申請がある日」の集合（カレンダーのドット用）。
  // ドットは常に未承認を示す（タブに追従しない）。ドットの有無でクリック時のタブ切替先を決める。
  const appDaysInMonth = new Set();
  apps.filter((a) => a.status === "pending").forEach((a) => {
    const r = appRange(a);
    if (!r.from) return;
    let cur = new Date(`${r.from}T00:00:00`);
    const end = new Date(`${r.to || r.from}T00:00:00`);
    while (cur <= end) {
      const dstr = `${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}-${pad2(cur.getDate())}`;
      if (`${dstr.slice(0, 4)}${dstr.slice(5, 7)}` === ym) appDaysInMonth.add(dstr);
      cur = new Date(cur.getTime() + 86400000);
    }
  });
  // シフト休（会社休）の集合と祝日名。カレンダーの「その日が出勤日か」を判定する。
  // 祝日は表示中の月の by-month から受け取る（過去・未来どちらの月も正確）。週末は RosterCalendar 側でローカル判定。
  const holidays = data.holidays || {};
  // 祝日でも出勤（予定）がある社員（{ 日付 -> 社員IDの配列 }）。祝日出勤の表示に使う。
  const holidayWork = data.holidayWork || {};
  // 承認済みの休みを日付展開して {日付 -> {社員ID -> チップ情報}} に（同姓同名で取り違えないよう id キー・#10）。
  const approvedLeaveByDay = {};
  apps.filter((a) => a.kind === "leave" && a.status === "approved").forEach((a) => {
    const meta = LEAVE_CHIP[a.type];
    if (!meta) return;
    let cur = new Date(`${a.date_from}T00:00:00`);
    const end = new Date(`${a.date_to || a.date_from}T00:00:00`);
    while (cur <= end) {
      const dstr = `${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}-${pad2(cur.getDate())}`;
      if (!approvedLeaveByDay[dstr]) approvedLeaveByDay[dstr] = {};
      approvedLeaveByDay[dstr][a.employee_id] = meta;
      cur = new Date(cur.getTime() + 86400000);
    }
  });
  // 実際に出勤（打刻）した (日付 -> 出勤した社員IDの集合)。祝日・週末でも出勤登録があれば表示するために使う。
  const workedByDay = {};
  days.forEach((day) => {
    const s = new Set();
    day.cells.forEach((c) => { if (c.status === "open" || c.status === "closed") s.add(c.employee_id); });
    workedByDay[day.date] = s;
  });
  // 未承認(pending)の申請がある (社員ID, 日付) の集合。グリッドのセル背景の色付け用（id キー・#10）。
  // 承認待ちが1件でもあれば色を付ける（承認済/却下だけの日は色を付けない）。休みは期間を全日展開する。
  const appCellKeys = new Set();
  apps.filter((a) => a.status === "pending").forEach((a) => {
    const r = appRange(a);
    if (!r.from) return;
    let cur = new Date(`${r.from}T00:00:00`);
    const end = new Date(`${r.to || r.from}T00:00:00`);
    while (cur <= end) {
      appCellKeys.add(`${a.employee_id}|${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}-${pad2(cur.getDate())}`);
      cur = new Date(cur.getTime() + 86400000);
    }
  });
  // 申請カードを右リストの中央へスクロールする（scrollIntoViewだとページ全体が動くため container.scrollBy を使う）。
  const scrollToApp = useCallback((key) => {
    const container = applistRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-appkey="${key}"]`);
    if (!el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const delta = (eRect.top - cRect.top) - (container.clientHeight - el.clientHeight) / 2;
    container.scrollBy({ top: delta, behavior: "smooth" });
  }, []);
  // スクロール対象が指定されたら、フィルタ切替の再レンダー確定後（二重rAF）にスクロール。
  // 直接呼ぶと setAppFilter の再レンダー前に発火してカードを掴めないため state 経由にする。
  useEffect(() => {
    if (!scrollTarget) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => scrollToApp(scrollTarget.key)));
    return () => cancelAnimationFrame(raf);
  }, [scrollTarget, scrollToApp]);
  // カレンダーの日付クリック。
  const onCalDayClick = (dstr) => {
    // 日報モード：日報の日付を切り替える（申請リストには切り替えない）。社員選択は維持。
    if (panelMode === "report") {
      if (days.some((d) => d.date === dstr)) {
        setReportDate(dstr);
        setHighlight({ from: dstr, to: dstr });
      }
      return;
    }
    setPanelMode("apps");
    const inDay = (x) => { const r = appRange(x); return r.from && dstr >= r.from && dstr <= (r.to || r.from); };
    // ドットのある日＝その日に未承認の申請がある。「未承認」タブに切り替えてその申請を選択＋ハイライト＋スクロール。
    const pendingHit = apps.find((x) => x.status === "pending" && inDay(x));
    if (pendingHit) {
      setAppFilter("pending");
      const key = `${pendingHit.kind}-${pendingHit.id}`;
      setSelectedAppKey(key);
      setHighlight(appRange(pendingHit));
      setScrollTarget({ key });
      return;
    }
    // ドットのない日＝未承認がない。「すべて」タブに切り替えて全申請（承認済み/却下含む）から
    // その日以降の最初（なければ最後）へスクロールする。選択・ハイライトは変えない。
    setAppFilter("all");
    const allSorted = [...apps].sort((a, b) => (appRange(a).from || "").localeCompare(appRange(b).from || ""));
    const near = allSorted.find((x) => (appRange(x).from || "") >= dstr) || allSorted[allSorted.length - 1];
    if (near) setScrollTarget({ key: `${near.kind}-${near.id}` });
  };

  return (
    <MypageLayout active="attendance" title="勤怠履歴">
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      {/* 月ナビ（案B）：◀ 年月▾ ▶ ＋今月。ラベルタップで月ピッカー（社員側と共通部品）。
          経営者は任意月へ飛べるので open モード（過去3年〜当月の範囲、点なし）。 */}
      <MonthPicker
        value={ymDash(ym)}
        minYm={`${parseInt(ymNow().slice(0, 4), 10) - 3}-01`}
        maxYm={ymDash(ymNow())}
        currentYm={ymDash(ymNow())}
        onChange={(v) => setYm(ymPlain(v))}
      />

      {loading ? (
        <Loader />
      ) : (
        <div className="mp-att-split mp-att-split-mgr">
          <div className="mp-att-tablecol">
            {/* 左カラム上部：月カレンダー（シフト×勤怠ロスター）。その下に勤怠一覧の表を縦に並べる。
                右カラム＝申請リスト／日報。未来月などデータが無い月でも常に表示する。 */}
            <RosterCalendar
              ym={ym}
              employees={employees}
              holidays={holidays}
              approvedByDay={approvedLeaveByDay}
              workedByDay={workedByDay}
              holidayWork={holidayWork}
              appDays={appDaysInMonth}
              range={highlight}
              onDayClick={onCalDayClick}
            />
            {days.length === 0 ? (
              <EmptyState>この月の勤怠記録はまだありません</EmptyState>
            ) : (
            <div className="mp-tablewrap">
              <table className="mp-table mp-att-grid">
                <thead>
                  <tr>
                    <th>日付</th>
                    {employees.map((e) => <th key={e.id}>{e.name}</th>)}
                    <th>日報</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day) => (
                    <tr key={day.date} className={panelMode === "report" && reportDate === day.date ? "mp-att-sel" : ""}>
                      <td className="mp-nowrap mp-att-datecol">{dateShort(day.date)}<span>({weekdayJa(day.date)})</span></td>
                      {employees.map((e) => {
                        const c = day.cells.find((x) => x.employee_id === e.id);
                        // その社員・その日に未承認(pending)の申請があればセルに色を付ける。
                        const hasApp = appCellKeys.has(`${e.id}|${day.date}`);
                        // セルを押すと右パネルを申請リストに戻す。
                        return <td key={e.id} className={`mp-nowrap mp-att-timecell${hasApp ? " mp-att-hasapp" : ""}`} onClick={() => onCellClick(e.id, day.date, hasApp)} title="申請リストを表示">{workText(c)}</td>;
                      })}
                      <td><button className="mp-btn mp-ghost mp-sm" onClick={() => openReport(day)}>詳細</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>

          {/* 右パネル（常設・2モード）。既定は申請リスト、詳細ボタンで日報へ。 */}
          <aside className="mp-att-detail mp-open" ref={detailRef}>
            {panelMode === "report" && reportDay ? (
              <>
                <div className="mp-att-detail-head">
                  <div className="mp-eyebrow">日報</div>
                  <h3>{dateShort(reportDay.date)}（{weekdayJa(reportDay.date)}）</h3>
                  {/* 社員切替ラジオ */}
                  <div className="mp-att-radio">
                    {employees.map((e) => (
                      <button key={e.id} className={`mp-att-radiobtn${reportEmpId === e.id ? " mp-on" : ""}`} onClick={() => setReportEmpId(e.id)}>{e.name}</button>
                    ))}
                  </div>
                </div>
                <div className="mp-att-detail-body">
                  {reportCell && reportCell.daily_report
                    ? <Markdown text={reportCell.daily_report} />
                    : <span className="mp-muted">この日の日報はありません。</span>}
                </div>
              </>
            ) : (
              <>
                <div className="mp-att-detail-head">
                  <h3>申請一覧</h3>
                  {/* フィルタタブ：申請中／承認済／却下／すべて。 */}
                  <div className="mp-att-appfilter">
                    {APP_FILTERS.map((f) => (
                      <button
                        key={f.key}
                        className={`mp-att-appfilter-tab${appFilter === f.key ? " mp-on" : ""}`}
                        onClick={() => setAppFilter(f.key)}
                      >{f.text}</button>
                    ))}
                  </div>
                </div>
                {sortedApps.length === 0 ? (
                  <div className="mp-att-detail-empty">該当する申請はありません。</div>
                ) : (
                  <div className="mp-att-applist" ref={applistRef}>
                    {sortedApps.map((a) => (
                      <div
                        className={`mp-att-app${selectedAppKey === `${a.kind}-${a.id}` ? " mp-att-app-sel" : ""}`}
                        key={`${a.kind}-${a.id}`}
                        data-appkey={`${a.kind}-${a.id}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => selectApp(a)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectApp(a); } }}
                      >
                        <div className="mp-att-app-top">
                          <span className={`mp-att-app-kind mp-k-${a.kind}`}>{APP_KIND[a.kind]}</span>
                          <span className="mp-att-app-name">{a.employee_name}</span>
                          {/* バッジは却下のみ。未承認はボタンの有無で、承認済みはボタンが消えることで判別できる。 */}
                          {a.status === "rejected" && <StatusBadge status="rejected" className="mp-att-app-status" />}
                          <span className="mp-att-app-date">{appDate(a)}</span>
                        </div>
                        <div className="mp-att-app-sum">{appSummary(a)}</div>
                        {/* 承認/却下は申請中のみ（承認済み・却下は処理済みなのでボタンなし）。 */}
                        {a.status === "pending" && (
                          <div className="mp-att-app-actions">
                            <button
                              className="mp-btn mp-accent mp-sm"
                              disabled={decidingKey === `${a.kind}-${a.id}`}
                              onClick={(e) => { e.stopPropagation(); setConfirm({ app: a, action: "approve" }); }}
                            >承認</button>
                            <button
                              className="mp-btn mp-danger mp-sm"
                              disabled={decidingKey === `${a.kind}-${a.id}`}
                              onClick={(e) => { e.stopPropagation(); setConfirm({ app: a, action: "reject" }); }}
                            >却下</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
      )}

      {/* 承認/却下の確認ダイアログ（既存の承認画面と同じ挙動）。 */}
      <ConfirmDialog
        open={!!confirm}
        ariaLabel="申請の確認"
        title={confirm && `この申請を${confirm.action === "approve" ? "承認" : "却下"}しますか？`}
        onClose={() => setConfirm(null)}
        onConfirm={doDecide}
        confirmLabel={confirm ? (decidingKey === `${confirm.app.kind}-${confirm.app.id}` ? "処理中…" : (confirm.action === "approve" ? "承認する" : "却下する")) : ""}
        confirmClass={confirm && confirm.action === "approve" ? "mp-accent" : "mp-danger"}
        confirmDisabled={confirm ? decidingKey === `${confirm.app.kind}-${confirm.app.id}` : false}
      >
        {confirm && `${confirm.app.employee_name} さんの${APP_KIND[confirm.app.kind]}申請を${confirm.action === "approve" ? "承認" : "却下"}します。`}
      </ConfirmDialog>
    </MypageLayout>
  );
}

export default AttendanceMonthly;
